/* ══════════════════════════════════════════════════════════════════
   PRODUCT-BUNDLE.JS — PDP cart-add behaviors
   Pairs with: snippets/product-bundle.liquid
               snippets/product-bundle-item.liquid
               sections/product-bundle.liquid (endpoint-only)
               sections/product-news.liquid (renders the snippet + ATC form)
               assets/product-bundle.css

   Two responsibilities:
     1. Bundle module — variant change, subtotal, add to cart (single + bundle),
        and tier-3 complementary-recs fallback fetch
     2. Main ATC AJAX intercept — catches the product form submit so the cart
        drawer opens instead of the page redirecting to /cart
   ══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ══════════════════════════════════════════════════════════════
     1. MAIN ATC FORM — AJAX INTERCEPT
     ══════════════════════════════════════════════════════════════ */

  // Capture-phase listener so we run before any other submit handler
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    // Skip if the form has explicitly opted out
    if (form.hasAttribute('data-no-ajax')) return;

    e.preventDefault();
    e.stopPropagation();
    submitProductForm(form);
  }, true);

  function submitProductForm(form) {
    var btn = form.querySelector('[name="add"]');
    var idInput = form.querySelector('[name="id"]:not([disabled])');
    if (!idInput || !idInput.value) return;

    var quantityInput = form.querySelector('[name="quantity"]');
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    var props = {};
    form.querySelectorAll('[name^="properties["]').forEach(function (el) {
      var match = el.name.match(/^properties\[(.+)\]$/);
      if (match) props[match[1]] = el.value;
    });

    setBusy(btn, true, 'ADDING…');

    var body = { id: parseInt(idInput.value, 10), quantity: quantity };
    if (Object.keys(props).length) body.properties = props;

    cartAdd([body])
      .then(function () {
        announceAdded(quantity);
        setBusy(btn, false, 'ADDED ✓');
        setTimeout(function () { restoreText(btn); }, 1500);
      })
      .catch(function (err) {
        setBusy(btn, false);
        restoreText(btn);
        alert((err && err.description) || 'Could not add to cart');
      });
  }

  /* ══════════════════════════════════════════════════════════════
     2. BUNDLE MODULE
     ══════════════════════════════════════════════════════════════ */

  var bundles = document.querySelectorAll('[data-product-bundle]');
  var DEBUG = /[?&]bundle_debug=1/.test(window.location.search);
  if (DEBUG) console.log('[pn-bundle] init: found', bundles.length, 'bundle(s)');
  bundles.forEach(initBundle);

  function initBundle(section) {
    if (section.hasAttribute('data-fallback')) {
      if (DEBUG) console.log('[pn-bundle] tier-3 fallback active, fetching…');
      loadComplementary(section).then(function () {
        var n = section.querySelectorAll('[data-bundle-item]').length;
        if (DEBUG) console.log('[pn-bundle] tier-3 result:', n, 'items');
        if (n === 0) {
          hideBundle(section, 'tier-3 returned empty');
          return;
        }
        showBundle(section);
        bindAll(section);
      });
    } else {
      var n = section.querySelectorAll('[data-bundle-item]').length;
      if (DEBUG) console.log('[pn-bundle] inline render produced', n, 'items');
      if (n === 0) {
        hideBundle(section, 'no items rendered server-side and no fallback');
        return;
      }
      bindAll(section);
    }
  }

  function hasAnyItems(section) {
    return section.querySelectorAll('[data-bundle-item]').length > 0;
  }

  function hideBundle(section, reason) {
    if (DEBUG) console.log('[pn-bundle] hiding —', reason);
    section.hidden = true;
    section.style.display = 'none';
  }

  function showBundle(section) {
    section.hidden = false;
    section.style.display = '';
  }

  /* ── Tier-3 fallback fetch ────────────────────────────────────── */

  function loadComplementary(section) {
    var pid = section.getAttribute('data-product-id');
    var max = parseInt(section.getAttribute('data-max-items'), 10) || 3;
    var base = (window.routes && window.routes.productRecommendationsUrl) || '/recommendations/products';
    var url = base
      + '?section_id=product-bundle'
      + '&intent=complementary'
      + '&product_id=' + encodeURIComponent(pid)
      + '&limit=' + max;

    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshItems = doc.querySelector('[data-bundle-items]');
        var ourItems = section.querySelector('[data-bundle-items]');
        if (!freshItems || !ourItems) return;
        moveChildrenInto(freshItems, ourItems);
        section.removeAttribute('data-fallback');

        // If items appeared, unhide the Add bundle button
        var addBtn = section.querySelector('[data-bundle-add-all]');
        if (addBtn && section.querySelectorAll('[data-bundle-item]').length > 0) {
          addBtn.hidden = false;
        }
      })
      .catch(function () { /* silent */ });
  }

  function moveChildrenInto(sourceEl, targetEl) {
    Array.prototype.slice.call(targetEl.childNodes).forEach(function (n) {
      targetEl.removeChild(n);
    });
    Array.prototype.slice.call(sourceEl.childNodes).forEach(function (n) {
      targetEl.appendChild(n);
    });
  }

  /* ── Wire all controls ────────────────────────────────────────── */

  function bindAll(section) {
    section.querySelectorAll('[data-bundle-variant-select]').forEach(function (sel) {
      sel.addEventListener('change', function () { onVariantChange(sel); });
    });

    section.querySelectorAll('[data-bundle-include]').forEach(function (cb) {
      cb.addEventListener('change', function () { recalcSubtotal(section); });
    });

    section.querySelectorAll('[data-bundle-add-single]').forEach(function (btn) {
      btn.addEventListener('click', function () { onAddSingle(btn); });
    });

    var addAll = section.querySelector('[data-bundle-add-all]');
    if (addAll) {
      addAll.addEventListener('click', function () { onAddBundle(section, addAll); });
    }

    watchMainProductForm(section);
    recalcSubtotal(section);
  }

  /* ── Variant change ───────────────────────────────────────────── */

  function onVariantChange(select) {
    var item = select.closest('[data-bundle-item]');
    if (!item) return;

    var variants = parseVariants(item);
    var chosenId = parseInt(select.value, 10);
    var v = variants.find(function (x) { return x.id === chosenId; });
    if (!v) return;

    item.setAttribute('data-current-variant-id', String(v.id));
    item.setAttribute('data-current-price-cents', String(v.price));
    item.setAttribute('data-current-available', v.available ? 'true' : 'false');

    var priceEl = item.querySelector('[data-bundle-item-price]');
    if (priceEl) priceEl.textContent = formatMoney(v.price);

    var img = item.querySelector('[data-bundle-item-image]');
    if (img && v.image) img.src = v.image;

    var checkbox = item.querySelector('[data-bundle-include]');
    var status = item.querySelector('[data-bundle-item-status]');
    if (v.available) {
      if (checkbox) { checkbox.disabled = false; }
      if (status) { status.hidden = true; status.textContent = ''; }
    } else {
      if (checkbox) { checkbox.disabled = true; checkbox.checked = false; }
      if (status) { status.hidden = false; status.textContent = 'Choose another size to add'; }
    }

    var addBtn = item.querySelector('[data-bundle-add-single]');
    if (addBtn) addBtn.setAttribute('data-variant-id', String(v.id));

    var section = item.closest('[data-product-bundle]');
    if (section) recalcSubtotal(section);
  }

  function parseVariants(item) {
    try { return JSON.parse(item.getAttribute('data-variants') || '[]'); }
    catch (e) { return []; }
  }

  /* ── Subtotal ─────────────────────────────────────────────────── */

  function recalcSubtotal(section) {
    var addBtn = section.querySelector('[data-bundle-add-all]');
    var anyChecked = false;
    var addonTotal = 0;
    var lineVariantIds = [];

    var mainVariantId = getMainVariantId();
    if (mainVariantId) lineVariantIds.push(mainVariantId);

    section.querySelectorAll('[data-bundle-item]').forEach(function (item) {
      var cb = item.querySelector('[data-bundle-include]');
      var available = item.getAttribute('data-current-available') === 'true';
      if (cb && cb.checked && available) {
        anyChecked = true;
        addonTotal += parseInt(item.getAttribute('data-current-price-cents'), 10) || 0;
        var vid = parseInt(item.getAttribute('data-current-variant-id'), 10);
        if (vid) lineVariantIds.push(vid);
      }
    });

    var mainCents = readMainPriceFromForm();
    if (mainCents === null) mainCents = readMainPriceFromDOM() || 0;
    var total = mainCents + addonTotal;

    var subEl = section.querySelector('[data-bundle-subtotal]');
    if (subEl) subEl.textContent = formatMoney(total);

    if (addBtn) {
      var hasItems = section.querySelectorAll('[data-bundle-item]').length > 0;
      addBtn.disabled = !anyChecked || !hasItems;
      addBtn.hidden = !hasItems;
    }

    // Ask the Storefront API for the discount-aware total. This catches
    // automatic cart-level discounts (e.g. "buy 2, save 10%") that simple
    // line summing can't see. Debounced; falls back silently on error.
    previewBundleTotal(section, lineVariantIds, total);
  }

  /* ── Discount-aware total via Storefront API cartCreate ────────── */

  var SF_TOKEN = (window.theme && window.theme.storefrontToken) || '';
  var SF_API = '/api/' + ((window.theme && window.theme.storefrontApiVersion) || '2025-01') + '/graphql.json';
  var previewTimer = null;
  var previewAbort = null;
  var previewSeq = 0;

  function previewBundleTotal(section, variantIds, faceValueCents) {
    if (!SF_TOKEN || !variantIds.length) return;
    if (previewTimer) clearTimeout(previewTimer);
    previewTimer = setTimeout(function () {
      runPreview(section, variantIds, faceValueCents);
    }, 250);
  }

  function runPreview(section, variantIds, faceValueCents) {
    if (previewAbort) previewAbort.abort();
    previewAbort = new AbortController();
    var seq = ++previewSeq;

    var query = 'mutation($lines:[CartLineInput!]!){cartCreate(input:{lines:$lines}){cart{cost{subtotalAmount{amount currencyCode}totalAmount{amount currencyCode}}discountAllocations{discountedAmount{amount}}}userErrors{message}}}';
    var lines = variantIds.map(function (id) {
      return { merchandiseId: 'gid://shopify/ProductVariant/' + id, quantity: 1 };
    });

    fetch(SF_API, {
      method: 'POST',
      signal: previewAbort.signal,
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SF_TOKEN
      },
      body: JSON.stringify({ query: query, variables: { lines: lines } })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (seq !== previewSeq) return; // stale
        var cart = res && res.data && res.data.cartCreate && res.data.cartCreate.cart;
        if (!cart) return;
        var subCents = Math.round(parseFloat(cart.cost.subtotalAmount.amount) * 100);
        var totalCents = Math.round(parseFloat(cart.cost.totalAmount.amount) * 100);
        applyPreview(section, faceValueCents, subCents, totalCents);
      })
      .catch(function (err) {
        if (err && err.name !== 'AbortError') {
          // Token may lack write-cart scope, or network blip — keep face value
          console.warn('[pn-bundle] discount preview failed:', err);
        }
      });
  }

  function applyPreview(section, faceValueCents, subCents, totalCents) {
    var subEl = section.querySelector('[data-bundle-subtotal]');
    if (!subEl) return;
    // totalAmount reflects automatic discounts; subtotalAmount is pre-discount
    var hasDiscount = totalCents < subCents;
    subEl.textContent = formatMoney(totalCents);
    var saving = subCents - totalCents;
    var note = section.querySelector('[data-bundle-discount-note]');
    if (hasDiscount && saving > 0) {
      if (!note) {
        note = document.createElement('span');
        note.setAttribute('data-bundle-discount-note', '');
        note.className = 'pn-bundle__totals-saving';
        var totals = section.querySelector('.pn-bundle__totals') || subEl.parentNode;
        if (totals) totals.appendChild(note);
      }
      note.textContent = 'Save ' + formatMoney(saving) + ' auto-applied';
    } else if (note) {
      note.remove();
    }
  }

  /* ── Single item add ──────────────────────────────────────────── */

  function onAddSingle(btn) {
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!variantId) return;

    setBusy(btn, true);
    cartAdd([{ id: variantId, quantity: 1 }])
      .then(function () {
        announceAdded(1);
        setBusy(btn, false, true);
      })
      .catch(function (err) {
        setBusy(btn, false);
        showError(btn.closest('[data-product-bundle]'), (err && err.description) || 'Could not add to cart');
      });
  }

  /* ── Bundle add ───────────────────────────────────────────────── */

  function onAddBundle(section, btn) {
    var items = [];

    var mainVariantId = getMainVariantId();
    if (mainVariantId) items.push({ id: mainVariantId, quantity: 1 });

    section.querySelectorAll('[data-bundle-item]').forEach(function (item) {
      var cb = item.querySelector('[data-bundle-include]');
      var available = item.getAttribute('data-current-available') === 'true';
      if (cb && cb.checked && available) {
        var vid = parseInt(item.getAttribute('data-current-variant-id'), 10);
        if (vid) items.push({ id: vid, quantity: 1 });
      }
    });

    if (items.length === 0) return;

    setBusy(btn, true, 'Adding…');
    cartAdd(items)
      .then(function () {
        announceAdded(items.length);
        setBusy(btn, false);
        flashSuccess(btn, 'Added ✓');
      })
      .catch(function (err) {
        setBusy(btn, false);
        showError(section, (err && err.description) || 'Could not add to cart');
      });
  }

  /* ── Cart-add helper ──────────────────────────────────────────── */

  function cartAdd(items) {
    var url = (window.routes && window.routes.cartAddUrl) || '/cart/add';
    return fetch(url + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (body) {
        if (!r.ok) throw body;
        return body;
      });
    });
  }

  function announceAdded(qty) {
    document.dispatchEvent(new CustomEvent('product:added', {
      bubbles: true,
      detail: { quantity: qty, source: 'product-bundle' }
    }));
  }

  /* ── Main product form helpers ────────────────────────────────── */

  function getMainVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]:not([disabled])');
    if (input && input.value) return parseInt(input.value, 10);
    return null;
  }

  function readMainPriceFromForm() {
    // Pull from product variants JSON exposed by product-news.liquid
    if (!window.__pnVariants) {
      var form = document.querySelector('form[action*="/cart/add"]');
      if (!form) return null;
    }
    var vid = getMainVariantId();
    if (!vid || !Array.isArray(window.__pnVariants)) return null;
    var match = window.__pnVariants.find(function (v) { return v.id === vid; });
    return match ? match.price : null;
  }

  function readMainPriceFromDOM() {
    var el = document.querySelector('.pn-price__amount')
          || document.querySelector('[data-product-price] .money')
          || document.querySelector('.product-price .money')
          || document.querySelector('[data-product-price]');
    if (!el) return null;
    var txt = (el.textContent || '').replace(/[^\d.]/g, '');
    if (!txt) return null;
    return Math.round(parseFloat(txt) * 100);
  }

  function watchMainProductForm(section) {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    var input = form.querySelector('[name="id"]');
    if (!input) return;

    var lastValue = input.value;
    var update = function () {
      if (input.value === lastValue) return;
      lastValue = input.value;
      recalcSubtotal(section);
    };

    input.addEventListener('change', update);
    var mo = new MutationObserver(update);
    mo.observe(input, { attributes: true, attributeFilter: ['value'] });
    setInterval(update, 800);
  }

  /* ── UI helpers ───────────────────────────────────────────────── */

  function setBusy(btn, busy, label) {
    if (!btn) return;
    if (busy) {
      btn.disabled = true;
      if (typeof label === 'string') {
        if (!btn.dataset.originalText) btn.dataset.originalText = btn.textContent;
        btn.textContent = label;
      }
      btn.classList.add('is-busy');
    } else {
      btn.disabled = false;
      btn.classList.remove('is-busy');
      if (label === true) {
        btn.classList.add('is-success');
        setTimeout(function () { btn.classList.remove('is-success'); }, 1500);
      }
    }
  }

  function flashSuccess(btn, label) {
    if (!btn) return;
    if (!btn.dataset.originalText) btn.dataset.originalText = btn.textContent;
    btn.textContent = label;
    btn.classList.add('is-success');
    setTimeout(function () {
      btn.classList.remove('is-success');
      restoreText(btn);
    }, 1500);
  }

  function restoreText(btn) {
    if (!btn) return;
    if (btn.dataset.originalText) {
      btn.textContent = btn.dataset.originalText;
      delete btn.dataset.originalText;
    }
  }

  function showError(section, msg) {
    if (!section) return;
    var el = section.querySelector('[data-bundle-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    setTimeout(function () { el.hidden = true; el.textContent = ''; }, 4000);
  }

  // Regex-based formatter that matches Shopify's money filter semantics. The
  // previous chained-string-replace version produced garbage (e.g.
  // "169.00169169,00${{amount}}") whenever the shop's money_format contained
  // more than one placeholder. Delegate to the global formatMoney from
  // custom.js when available, with an inline regex fallback.
  function formatMoney(cents) {
    var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    if (typeof window.formatMoney === 'function' && window.formatMoney !== formatMoney) {
      return window.formatMoney(cents, fmt);
    }
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return fmt;
    function withDelimiters(precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(cents) || cents == null) return '0';
      var n = (cents / 100).toFixed(precision).split('.');
      var d = n[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return d + (n[1] ? decimal + n[1] : '');
    }
    var value;
    switch (match[1]) {
      case 'amount': value = withDelimiters(2); break;
      case 'amount_no_decimals': value = withDelimiters(0); break;
      case 'amount_with_comma_separator': value = withDelimiters(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(2, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(0, ' '); break;
      default: value = withDelimiters(2);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

})();
